import {useEffect, type CSSProperties} from 'react';
import {Outlet, useLocation, useParams} from 'react-router-dom';
import {trpc} from '@common-web/lib/trpc';
import {getGroupConfig} from '@client/pages/public/enroll/groups/registry';
import {EnrollGroupProvider} from '@client/pages/public/enroll/enrollContext';
import {EnrollPreviewProvider, useEnrollPreview} from '@client/pages/public/enroll/enrollPreview';
import {PreviewBanner} from '@client/pages/public/enroll/components/PreviewBanner';
import {useAnalytics} from '@common-web/hooks/useAnalytics';
import {NotFound} from '@client/pages/public/enroll/components/NotFound';
import {Header} from '@client/pages/public/enroll/components/Header';
import {Footer} from '@client/pages/public/enroll/components/Footer';
import {AppDownloadFab} from '@client/pages/public/enroll/components/appDownload';
import {enrollBrandVars} from '@client/pages/public/enroll/brand';
import '@client/pages/public/enroll/assets/fonts/jost.css';

const ENROLL_FONT_STACK =
  '"Century Gothic", "CenturyGothic", "Jost", "Avant Garde", "Futura", "Trebuchet MS", "Arial", system-ui, sans-serif';

export function EnrollLayout() {
  const {groupSlug} = useParams<{groupSlug: string}>();
  const preview = useEnrollPreview();
  // DB-published config first; the hard-coded registry is a fallback until the DB
  // path is verified in every environment (cutover removes the registry). In preview,
  // the config is injected (the saved draft) and no fetch happens.
  const siteQuery = trpc.enrollment.getSiteConfig.useQuery(
    {groupSlug: groupSlug ?? ''},
    {enabled: !!groupSlug && !preview.config, retry: false, staleTime: 5 * 60 * 1000},
  );
  const group = preview.config ?? siteQuery.data ?? (siteQuery.isError ? getGroupConfig(groupSlug) : undefined);
  const location = useLocation();
  const {register, unregister} = useAnalytics();
  // Banner only on the landing and access-code views, not mid-flow.
  const lastSegment = location.pathname.split('/').filter(Boolean).pop() ?? '';
  const showBanner = lastSegment === groupSlug || lastSegment === 'access-code';

  useEffect(() => {
    document.title = group ? `${group.name} — Benefits Enrollment` : 'Benefits Enrollment';
  }, [group]);

  useEffect(() => {
    if (!group) return;
    register({group_slug: group.slug});
    return () => unregister('group_slug');
  }, [group, register, unregister]);

  // Resolving config — mirror index.html's #app-loading spinner so the bundle-load
  // and config-load waits read as one continuous state, not two. Brand color isn't
  // known yet, so it stays neutral.
  if (!group && siteQuery.isLoading) {
    return (
      <div className='grid min-h-screen place-items-center bg-neutral-50'>
        <div className='h-7 w-7 animate-spin rounded-full border-[3px] border-neutral-200 border-t-neutral-500' />
      </div>
    );
  }

  if (!group) {
    return <NotFound slug={groupSlug} />;
  }

  const brandStyle = {
    ...enrollBrandVars(group.primaryColor),
    fontFamily: ENROLL_FONT_STACK,
  } as CSSProperties;

  // A published demo site reuses preview's gate-skip + stubbed submit, but served
  // publicly and fully interactive. The builder's own preview wins if both are set.
  const demoMode = !preview.isPreview && !!group.isDemo;
  const previewValue = demoMode ? {isPreview: true, isDemo: true} : preview;

  return (
    <EnrollPreviewProvider value={previewValue}>
    <EnrollGroupProvider value={group}>
      <div
        style={brandStyle}
        className='flex min-h-screen flex-col overflow-x-clip bg-neutral-50 text-neutral-900'
      >
        <PreviewBanner />
        <Header />

        {group.banner && showBanner && (
          <div className='bg-[var(--enroll-accent)] px-6 py-2.5 text-center text-base font-bold uppercase tracking-wide text-white'>
            {group.banner}
          </div>
        )}

        <main className='mx-auto w-full max-w-7xl flex-1 px-6 py-10'>
          <Outlet />
        </main>

        <Footer />
        <AppDownloadFab />
      </div>
    </EnrollGroupProvider>
    </EnrollPreviewProvider>
  );
}
